import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import { carBrandsAPI } from "../../services/api";
import { CarBrand } from "../../types";

const CarBrands: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState<CarBrand | null>(null);
  const [brands, setBrands] = useState<CarBrand[]>([]);
  const [form, setForm] = useState({ name: "", logo_url: "", status: true });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const data = await carBrandsAPI.getAll();
      setBrands(data.map((b: any) => ({ ...b, status: Boolean(b.status) })));
    } finally {
      setLoading(false);
    }
  };

  const openForm = (brand?: CarBrand) => {
    if (brand) {
      setEditing(brand);
      setForm({
        name: brand.name,
        logo_url: brand.logo_url || "",
        status: brand.status,
      });
    } else {
      setEditing(null);
      setForm({ name: "", logo_url: "", status: true });
    }
    setOpenDialog(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) await carBrandsAPI.update(editing.brand_id, form);
      else await carBrandsAPI.create(form);
      fetchBrands();
      setOpenDialog(false);
    } finally {
      setLoading(false);
    }
  };

  const deleteBrand = async (id: number) => {
    if (!window.confirm("Delete this brand?")) return;
    await carBrandsAPI.delete(id);
    fetchBrands();
  };

  const toggleStatus = async (brand: CarBrand) => {
    await carBrandsAPI.update(brand.brand_id, {
      ...brand,
      status: !brand.status,
    });
    fetchBrands();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={3}>
        Car Brands
      </Typography>

      <Button variant="contained" onClick={() => openForm()} startIcon={<Add />}>
        Add Brand
      </Button>

      <Paper sx={{ mt: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.100" }}>
                <TableCell>Brand Name</TableCell>
                <TableCell>Logo URL</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {brands.map((brand) => (
                <TableRow key={brand.brand_id}>
                  <TableCell>{brand.name}</TableCell>
                  <TableCell>{brand.logo_url || "-"}</TableCell>
                  <TableCell>
                    <Chip
                      label={brand.status ? "Active" : "Inactive"}
                      color={brand.status ? "success" : "default"}
                      onClick={() => toggleStatus(brand)}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton color="primary" onClick={() => openForm(brand)}>
                      <Edit />
                    </IconButton>
                    <IconButton color="error" onClick={() => deleteBrand(brand.brand_id)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {brands.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No brands found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? "Edit Brand" : "Add Brand"}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              label="Brand Name"
              name="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Logo URL"
              name="logo_url"
              value={form.logo_url}
              onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editing ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default CarBrands;